import { useState } from 'react';
import { editablePosts, toneOptions, themeOptions, ctaPresets } from '@/mocks/contentEngineImplementation';

export default function ContentEditor() {
  const [selectedPost, setSelectedPost] = useState(editablePosts[0]);
  const [activeTab, setActiveTab] = useState('caption');
  const [captionText, setCaptionText] = useState(selectedPost.currentCaption);
  const [creativeText, setCreativeText] = useState(selectedPost.currentCreative);
  const [scriptText, setScriptText] = useState(selectedPost.currentScript);
  const [selectedTheme, setSelectedTheme] = useState(selectedPost.currentTheme);
  const [selectedTone, setSelectedTone] = useState(selectedPost.currentTone);
  const [ctaText, setCtaText] = useState(selectedPost.currentCTA);
  const [hashtags, setHashtags] = useState<string[]>([...selectedPost.currentHashtags]);
  const [newHashtag, setNewHashtag] = useState('');
  const [showAssetPanel, setShowAssetPanel] = useState(false);

  const handlePostChange = (post: typeof editablePosts[0]) => {
    setSelectedPost(post);
    setCaptionText(post.currentCaption);
    setCreativeText(post.currentCreative);
    setScriptText(post.currentScript);
    setSelectedTheme(post.currentTheme);
    setSelectedTone(post.currentTone);
    setCtaText(post.currentCTA);
    setHashtags([...post.currentHashtags]);
    setActiveTab('caption');
  };

  const addHashtag = () => {
    if (newHashtag && !hashtags.includes(newHashtag)) {
      setHashtags([...hashtags, newHashtag.startsWith('#') ? newHashtag : `#${newHashtag}`]);
      setNewHashtag('');
    }
  };

  const removeHashtag = (tag: string) => {
    setHashtags(hashtags.filter((h) => h !== tag));
  };

  const regenerateHashtags = () => {
    setHashtags([...selectedPost.suggestedHashtags]);
  };

  const tabs = [
    { id: 'caption', name: 'Caption & Copy', icon: 'ri-file-text-line' },
    { id: 'theme', name: 'Theme Override', icon: 'ri-palette-line' },
    { id: 'tone', name: 'Tone & Voice', icon: 'ri-mic-line' },
    { id: 'cta', name: 'CTA & Messaging', icon: 'ri-chat-1-line' },
    { id: 'hashtags', name: 'Hashtag Engine', icon: 'ri-hashtag' },
    { id: 'assets', name: 'Creative Assets', icon: 'ri-image-line' },
  ];

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-160px)] min-h-[600px]">
      {/* Left: Post Selector */}
      <div className="w-full lg:w-72 flex-shrink-0 bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-900">Posts to Edit</h3>
          <p className="text-xs text-gray-500 mt-0.5">{editablePosts.length} posts ready for refinement</p>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {editablePosts.map((post) => (
            <button
              key={post.id}
              onClick={() => handlePostChange(post)}
              className={`w-full text-left p-3 rounded-lg border transition-all ${
                selectedPost.id === post.id
                  ? 'border-blue-300 bg-blue-50 ring-1 ring-blue-200'
                  : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <i className={`${post.platformIcon} text-sm ${
                  post.platform === 'LinkedIn' ? 'text-sky-600' :
                  post.platform === 'Instagram' ? 'text-pink-600' :
                  post.platform === 'Twitter' ? 'text-gray-600' :
                  'text-red-600'
                }`}></i>
                <span className="text-xs font-medium text-gray-600">{post.platform}</span>
              </div>
              <p className="text-sm font-medium text-gray-900 line-clamp-2">{post.title}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{post.currentTheme}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{post.currentTone}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Center: Preview */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                selectedPost.platform === 'LinkedIn' ? 'bg-sky-50' :
                selectedPost.platform === 'Instagram' ? 'bg-pink-50' :
                selectedPost.platform === 'Twitter' ? 'bg-gray-100' :
                'bg-red-50'
              }`}>
                <i className={`${selectedPost.platformIcon} text-xl ${
                  selectedPost.platform === 'LinkedIn' ? 'text-sky-600' :
                  selectedPost.platform === 'Instagram' ? 'text-pink-600' :
                  selectedPost.platform === 'Twitter' ? 'text-gray-700' :
                  'text-red-600'
                }`}></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selectedPost.title}</h3>
                <p className="text-xs text-gray-500">Theme: {selectedTheme} | Tone: {selectedTone}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors whitespace-nowrap">
                <i className="ri-ai-generate mr-1"></i>AI Regenerate
              </button>
              <button className="px-3 py-1.5 text-sm font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors whitespace-nowrap">
                <i className="ri-check-line mr-1"></i>Save
              </button>
            </div>
          </div>

          {/* Preview Cards */}
          <div className="space-y-3">
            <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Caption</span>
              <p className="text-sm text-gray-900 mt-2 leading-relaxed">{captionText}</p>
            </div>
            <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Creative Copy</span>
              <p className="text-sm text-gray-900 mt-2 leading-relaxed">{creativeText}</p>
            </div>
            {scriptText && (
              <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Script / Structure</span>
                <p className="text-sm text-gray-900 mt-2 leading-relaxed whitespace-pre-line">{scriptText}</p>
              </div>
            )}
            <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">CTA</span>
              <p className="text-sm text-gray-900 mt-2">{ctaText}</p>
            </div>
            <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Hashtags</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {hashtags.map((tag) => (
                  <span key={tag} className="text-sm text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* AI Suggestions */}
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-lightbulb-line text-amber-600"></i>
            <span className="text-sm font-semibold text-amber-900">AI Suggestions</span>
          </div>
          <div className="space-y-2">
            {selectedPost.aiSuggestions.map((suggestion, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm text-amber-800">
                <span className="text-amber-600 font-medium mt-0.5">{idx + 1}.</span>
                <span>{suggestion}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Editing Panel */}
      <div className="w-full lg:w-80 flex-shrink-0 bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-3 text-xs font-medium whitespace-nowrap transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <i className={tab.icon}></i>
              {tab.name}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {activeTab === 'caption' && (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1.5">Caption</label>
                <textarea
                  value={captionText}
                  onChange={(e) => setCaptionText(e.target.value)}
                  className="w-full h-24 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Edit your caption..."
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{captionText.length} chars</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1.5">Creative Copy</label>
                <textarea
                  value={creativeText}
                  onChange={(e) => setCreativeText(e.target.value)}
                  className="w-full h-28 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Edit creative copy..."
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{creativeText.length} chars</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1.5">Script / Structure</label>
                <textarea
                  value={scriptText}
                  onChange={(e) => setScriptText(e.target.value)}
                  className="w-full h-28 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Edit script or content structure..."
                />
              </div>
              <button className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors whitespace-nowrap">
                <i className="ri-ai-generate mr-1.5"></i>Regenerate with AI
              </button>
            </div>
          )}

          {activeTab === 'theme' && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">Override the content theme for this post.</p>
              <div className="space-y-2">
                {themeOptions.map((theme) => (
                  <button
                    key={theme}
                    onClick={() => setSelectedTheme(theme)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                      selectedTheme === theme
                        ? 'border-blue-300 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>{theme}</span>
                    {selectedTheme === theme && <i className="ri-check-line text-blue-600"></i>}
                  </button>
                ))}
              </div>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-600">Current: <span className="font-medium">{selectedPost.currentTheme}</span></p>
                <p className="text-xs text-gray-600 mt-1">New: <span className="font-medium text-blue-600">{selectedTheme}</span></p>
              </div>
            </div>
          )}

          {activeTab === 'tone' && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">Adjust the tone and brand voice intensity.</p>
              <div className="space-y-2">
                {toneOptions.map((tone) => (
                  <button
                    key={tone}
                    onClick={() => setSelectedTone(tone)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                      selectedTone === tone
                        ? 'border-blue-300 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>{tone}</span>
                    {selectedTone === tone && <i className="ri-check-line text-blue-600"></i>}
                  </button>
                ))}
              </div>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-600">Current: <span className="font-medium">{selectedPost.currentTone}</span></p>
                <p className="text-xs text-gray-600 mt-1">New: <span className="font-medium text-blue-600">{selectedTone}</span></p>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1.5">Brand Voice Intensity</label>
                <input type="range" min="1" max="10" defaultValue="7" className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  <span>Subtle</span>
                  <span>Strong</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'cta' && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">Edit your call-to-action or select a preset.</p>
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1.5">CTA Text</label>
                <textarea
                  value={ctaText}
                  onChange={(e) => setCtaText(e.target.value)}
                  className="w-full h-20 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1.5">Presets</label>
                <div className="space-y-1.5">
                  {ctaPresets.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setCtaText(preset)}
                      className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 text-xs text-gray-700 hover:bg-blue-50 hover:border-blue-200 transition-all"
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'hashtags' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">Manage hashtags for this post.</p>
                <button
                  onClick={regenerateHashtags}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap"
                >
                  <i className="ri-ai-generate mr-1"></i>Auto-generate
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {hashtags.map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-1 text-sm text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg">
                    {tag}
                    <button onClick={() => removeHashtag(tag)} className="text-blue-400 hover:text-blue-600">
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  value={newHashtag}
                  onChange={(e) => setNewHashtag(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addHashtag()}
                  placeholder="Add hashtag..."
                  className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={addHashtag}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                >
                  <i className="ri-add-line"></i>
                </button>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1.5">AI Suggested Hashtags</p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedPost.suggestedHashtags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => {
                        if (!hashtags.includes(tag)) setHashtags([...hashtags, tag]);
                      }}
                      className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    >
                      + {tag}
                    </button>
                  ))}
                </div>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-xs text-emerald-800 font-medium">Performance Tip</p>
                <p className="text-xs text-emerald-600 mt-1">Posts with 4-8 hashtags see 23% more reach on {selectedPost.platform}.</p>
              </div>
            </div>
          )}

          {activeTab === 'assets' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">Linked creative assets for this post.</p>
                <button
                  onClick={() => setShowAssetPanel(!showAssetPanel)}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap"
                >
                  <i className="ri-upload-cloud-line mr-1"></i>Upload
                </button>
              </div>

              {selectedPost.assets.map((asset) => (
                <div key={asset.id} className="p-3 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <i className={`${asset.type === 'image' ? 'ri-image-line' : asset.type === 'video' ? 'ri-video-line' : 'ri-stack-line'} text-gray-500`}></i>
                      <span className="text-sm font-medium text-gray-900">{asset.name}</span>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      asset.status === 'uploaded' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}>{asset.status}</span>
                  </div>
                  {asset.url && (
                    <img src={asset.url} alt={asset.name} className="w-full h-28 object-cover rounded-lg mb-2" />
                  )}
                  {!asset.url && (
                    <div className="w-full h-28 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xs">
                      No preview available
                    </div>
                  )}
                  <div className="flex gap-2 mt-2">
                    <button className="flex-1 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors whitespace-nowrap">
                      <i className="ri-edit-line mr-1"></i>Edit
                    </button>
                    <button className="flex-1 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors whitespace-nowrap">
                      <i className="ri-ai-generate mr-1"></i>AI Edit
                    </button>
                  </div>
                </div>
              ))}

              {/* Asset Versioning */}
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2">Asset Versioning</p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <i className="ri-check-line text-emerald-500"></i>
                    <span>Original asset</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <i className="ri-check-line text-emerald-500"></i>
                    <span>Edited asset (AI adjusted)</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <i className="ri-check-line text-emerald-500"></i>
                    <span>Final selected asset</span>
                  </div>
                </div>
              </div>

              {/* Format Builder */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs font-semibold text-blue-800 mb-1.5">Content Format Builder</p>
                <p className="text-xs text-blue-600 mb-2">Changing format auto-adjusts caption length, structure, and engagement style.</p>
                <select className="w-full px-3 py-2 text-sm border border-blue-200 rounded-lg bg-white text-blue-900">
                  <option>Static Image</option>
                  <option>Carousel</option>
                  <option>Infographic</option>
                  <option>Video</option>
                  <option>Reel / Short</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}